import toast from './toast';
import {
  ALIGN,
  STANDARD_MARGIN,
  DEFAULT_TABLE_STYLES,
  createBrandedPDF,
  drawSeparatorLine,
  addBrandedFooter,
  loadExcelDependency,
  fmtCurrency,
  fmtDays,
  fmtDate,
} from './pdfReportTemplate';

function buildFilenameBase(reportData) {
  const start = (reportData.period?.start || '').replace(/-/g, '');
  const end = (reportData.period?.end || '').replace(/-/g, '');
  return `PayrollLeaveDeductions_${start}_${end}`;
}

const FLAT_COLUMNS = [
  { key: 'staff_id',           label: 'Staff ID',        halign: ALIGN.TEXT },
  { key: 'employee_name',      label: 'Employee Name',   halign: ALIGN.TEXT },
  { key: 'department',         label: 'Department',       halign: ALIGN.TEXT },
  { key: 'classification',     label: 'Classification',  halign: ALIGN.TEXT },
  { key: 'origin_leave_type',  label: 'Derived From',     halign: ALIGN.TEXT },
  { key: 'leave_type_name',    label: 'Leave Type',       halign: ALIGN.TEXT },
  { key: 'leave_start_display', label: 'Leave Start',     halign: ALIGN.DATE },
  { key: 'leave_end_display',   label: 'Leave End',       halign: ALIGN.DATE },
  { key: 'total_leave_days',   label: 'Application Days', halign: ALIGN.NUMBER, fmt: fmtDays },
  { key: 'days_in_period',     label: 'Deductible Days', halign: ALIGN.NUMBER, fmt: fmtDays },
  { key: 'basic_salary',       label: 'Basic Salary',    halign: ALIGN.CURRENCY, fmt: fmtCurrency },
  { key: 'daily_rate',         label: 'Daily Rate',      halign: ALIGN.CURRENCY, fmt: fmtCurrency },
  { key: 'deduction_amount',   label: 'Deduction ($)',   halign: ALIGN.CURRENCY, fmt: fmtCurrency },
];

function fmtDateWithSession(dateStr, timePeriod, isStart) {
  if (!dateStr) return '-';
  const formatted = fmtDate(dateStr);
  const isHalf = isStart ? timePeriod === 'PM' : timePeriod === 'AM';
  return isHalf ? `${formatted} (${timePeriod})` : formatted;
}

function flattenRows(reportData) {
  const rows = [];
  for (const emp of reportData.employees || []) {
    for (const item of emp.items || []) {
      rows.push({
        staff_id: emp.staff_id,
        employee_name: emp.employee_name,
        department: emp.department,
        classification: emp.classification,
        origin_leave_type: item.origin_leave_type,
        leave_type_name: item.leave_type_name || item.leave_type_display,
        leave_start_display: fmtDateWithSession(item.leave_start_date, item.leave_start_time_period, true),
        leave_end_display: fmtDateWithSession(item.leave_end_date, item.leave_end_time_period, false),
        total_leave_days: item.total_leave_days,
        days_in_period: item.days_in_period,
        basic_salary: emp.basic_salary,
        daily_rate: emp.daily_rate,
        deduction_amount: item.deduction_amount,
      });
    }
  }
  return rows;
}

function rowToArray(row) {
  return FLAT_COLUMNS.map((col) => {
    const raw = row[col.key];
    if (raw == null) return '-';
    return col.fmt ? col.fmt(raw) : raw;
  });
}

// ─── PDF detail columns (slightly trimmed vs Excel — salary/rate on summary) ─

const PDF_DETAIL_COLUMNS = [
  { key: 'staff_id',            label: 'Staff ID',         halign: ALIGN.TEXT },
  { key: 'employee_name',       label: 'Name',             halign: ALIGN.TEXT },
  { key: 'department',          label: 'Dept',             halign: ALIGN.TEXT },
  { key: 'classification',      label: 'Class',            halign: ALIGN.TEXT },
  { key: 'origin_leave_type',   label: 'Derived From',     halign: ALIGN.TEXT },
  { key: 'leave_type_name',     label: 'Leave Type',       halign: ALIGN.TEXT },
  { key: 'leave_start_display', label: 'Leave Start',      halign: ALIGN.DATE },
  { key: 'leave_end_display',   label: 'Leave End',        halign: ALIGN.DATE },
  { key: 'total_leave_days',    label: 'Appl. Days',       halign: ALIGN.NUMBER, fmt: fmtDays },
  { key: 'days_in_period',      label: 'Deduct. Days',     halign: ALIGN.NUMBER, fmt: fmtDays },
  { key: 'daily_rate',          label: 'Daily Rate',       halign: ALIGN.CURRENCY, fmt: fmtCurrency },
  { key: 'deduction_amount',    label: 'Deduction ($)',    halign: ALIGN.CURRENCY, fmt: fmtCurrency },
];

function pdfRowToArray(row) {
  return PDF_DETAIL_COLUMNS.map((col) => {
    const raw = row[col.key];
    if (raw == null) return '-';
    return col.fmt ? col.fmt(raw) : raw;
  });
}

function buildGroupedPdfRows(reportData) {
  const body = [];
  const subtotalIndices = [];

  for (const emp of reportData.employees || []) {
    const items = emp.items || [];
    for (const item of items) {
      body.push(pdfRowToArray({
        staff_id: emp.staff_id,
        employee_name: emp.employee_name,
        department: emp.department,
        classification: emp.classification,
        origin_leave_type: item.origin_leave_type,
        leave_type_name: item.leave_type_name || item.leave_type_display,
        leave_start_display: fmtDateWithSession(item.leave_start_date, item.leave_start_time_period, true),
        leave_end_display: fmtDateWithSession(item.leave_end_date, item.leave_end_time_period, false),
        total_leave_days: item.total_leave_days,
        days_in_period: item.days_in_period,
        daily_rate: emp.daily_rate,
        deduction_amount: item.deduction_amount,
      }));
    }

    const totalDays = parseFloat(emp.subtotal_unpaid_days || 0) + parseFloat(emp.subtotal_ns_days || 0);
    body.push([
      { content: `${emp.employee_name} — Sub-total`, colSpan: 9, styles: { fontStyle: 'bold', halign: 'right' } },
      { content: fmtDays(totalDays), styles: { fontStyle: 'bold' } },
      '',
      { content: fmtCurrency(emp.total_deduction), styles: { fontStyle: 'bold' } },
    ]);
    subtotalIndices.push(body.length - 1);
  }

  return { body, subtotalIndices };
}

// ─── PDF Export ───────────────────────────────────────────────────────────────

function computeCategoryCounts(reportData) {
  let unpaidCount = 0;
  let nsCount = 0;
  for (const emp of reportData.employees || []) {
    if (parseFloat(emp.subtotal_unpaid_days || 0) > 0) unpaidCount++;
    if (parseFloat(emp.subtotal_ns_days || 0) > 0) nsCount++;
  }
  return { unpaidCount, nsCount };
}

export async function exportPayrollLeaveDeductionPDF(reportData) {
  const summary = reportData.summary || {};
  const periodStart = reportData.period?.start || '';
  const periodEnd = reportData.period?.end || '';
  const periodLabel = periodStart && periodEnd
    ? `${fmtDate(periodStart)} — ${fmtDate(periodEnd)}`
    : 'Unknown Period';

  const { unpaidCount, nsCount } = computeCategoryCounts(reportData);

  const layout = await createBrandedPDF({
    title: 'Payroll Leave Deductions',
    subtitle: `Period: ${periodLabel}`,
    orientation: 'landscape',
  });
  const { doc, autoTable: autoTableFn } = layout;

  let y = layout.headerEndY + 4;

  // ── Summary table ──
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('Report Summary', layout.leftX, y);
  y += 6;

  const summaryHead = [['Category', 'Employees', 'Days', 'Deduction ($)']];
  const summaryBody = [
    ['Unpaid Leave', String(unpaidCount), fmtDays(summary.total_unpaid_days), fmtCurrency(summary.total_unpaid_deduction)],
    ['NS / ICT Leave', String(nsCount), fmtDays(summary.total_ns_days), fmtCurrency(summary.total_ns_deduction)],
    [
      { content: 'Grand Total', styles: { fontStyle: 'bold' } },
      { content: String(summary.total_employees || 0), styles: { fontStyle: 'bold' } },
      { content: fmtDays(parseFloat(summary.total_unpaid_days || 0) + parseFloat(summary.total_ns_days || 0)), styles: { fontStyle: 'bold' } },
      { content: fmtCurrency(summary.grand_total_deduction), styles: { fontStyle: 'bold' } },
    ],
  ];

  autoTableFn(doc, {
    startY: y,
    head: summaryHead,
    body: summaryBody,
    ...DEFAULT_TABLE_STYLES,
    margin: layout.tableMargin,
    tableWidth: 200,
    columnStyles: {
      0: { halign: ALIGN.TEXT },
      1: { halign: ALIGN.NUMBER },
      2: { halign: ALIGN.NUMBER },
      3: { halign: ALIGN.CURRENCY },
    },
  });

  // ── Detail pages ──
  doc.addPage('landscape');
  const leftX = STANDARD_MARGIN;
  const rightX = doc.internal.pageSize.getWidth() - STANDARD_MARGIN;
  let detailY = 14;

  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('Employee Deduction Detail', leftX, detailY);
  detailY += 6;
  drawSeparatorLine(doc, detailY, leftX, rightX);

  const { body: detailBody, subtotalIndices } = buildGroupedPdfRows(reportData);

  const grandTotalDays = parseFloat(summary.total_unpaid_days || 0) + parseFloat(summary.total_ns_days || 0);
  detailBody.push([
    { content: `Grand Total — ${summary.total_employees || 0} employee(s)`, colSpan: 9, styles: { fontStyle: 'bold', halign: 'right', textColor: 255 } },
    { content: fmtDays(grandTotalDays), styles: { fontStyle: 'bold', textColor: 255 } },
    '',
    { content: fmtCurrency(summary.grand_total_deduction), styles: { fontStyle: 'bold', textColor: 255 } },
  ]);
  const grandTotalIdx = detailBody.length - 1;

  const colStyles = {};
  PDF_DETAIL_COLUMNS.forEach((col, i) => { colStyles[i] = { halign: col.halign }; });

  autoTableFn(doc, {
    startY: detailY,
    head: [PDF_DETAIL_COLUMNS.map((c) => c.label)],
    body: detailBody,
    ...DEFAULT_TABLE_STYLES,
    margin: layout.tableMargin,
    columnStyles: colStyles,
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      const idx = data.row.index;
      if (subtotalIndices.includes(idx)) {
        data.cell.styles.fillColor = [243, 244, 246];
        data.cell.styles.fontStyle = 'bold';
      }
      if (idx === grandTotalIdx) {
        data.cell.styles.fillColor = [31, 41, 55];
        data.cell.styles.textColor = 255;
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  addBrandedFooter(doc, layout);

  const filename = `${buildFilenameBase(reportData)}.pdf`;
  doc.save(filename);
  toast.success('PDF downloaded');
}

// ─── Excel ────────────────────────────────────────────────────────────────────

export async function exportPayrollLeaveDeductionExcel(reportData) {
  const { XLSX } = await loadExcelDependency();
  const rows = flattenRows(reportData);

  const headers = FLAT_COLUMNS.map((c) => c.label);
  const data = [headers, ...rows.map(rowToArray)];

  const summary = reportData.summary || {};
  const summaryData = [
    ['Payroll Leave Deductions Report'],
    ['Period', `${reportData.period?.start || ''} to ${reportData.period?.end || ''}`],
    ['Employees Affected', summary.total_employees || 0],
    ['Total Unpaid Leave Days', fmtDays(summary.total_unpaid_days)],
    ['Total Unpaid Deduction', fmtCurrency(summary.total_unpaid_deduction)],
    ['Total NS Days', fmtDays(summary.total_ns_days)],
    ['Total NS Deduction', fmtCurrency(summary.total_ns_deduction)],
    ['Grand Total Deduction', fmtCurrency(summary.grand_total_deduction)],
  ];

  const wb = XLSX.utils.book_new();

  const wsData = XLSX.utils.aoa_to_sheet(data);
  XLSX.utils.book_append_sheet(wb, wsData, 'Leave Deductions');

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

  const filename = `${buildFilenameBase(reportData)}.xlsx`;
  XLSX.writeFile(wb, filename);
}

// ─── CSV ──────────────────────────────────────────────────────────────────────

export function exportPayrollLeaveDeductionCSV(reportData) {
  const rows = flattenRows(reportData);
  const headers = FLAT_COLUMNS.map((c) => c.label);

  const csvRows = [headers.join(',')];
  for (const row of rows) {
    const values = rowToArray(row).map((v) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    });
    csvRows.push(values.join(','));
  }

  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${buildFilenameBase(reportData)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
